import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DeviceMobile, DesktopTower, Laptop, ShieldCheck, ShieldSlash,
  Trash, PencilSimple, Check, X, Devices
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconFor = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('iphone') || n.includes('android')) return DeviceMobile;
  if (n.includes('ipad')) return Laptop;
  if (n.includes('mac') || n.includes('windows') || n.includes('linux')) return Laptop;
  return DesktopTower;
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} d ago`;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
  } catch {
    return iso;
  }
};

const RegisteredDevicesSection = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('admin'); // 'admin' | 'handlers'
  const [devices, setDevices] = useState([]);
  const [handlerDevices, setHandlerDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [sessionModal, setSessionModal] = useState(null);
  const [deletePasswordDev, setDeletePasswordDev] = useState(null); // point 44 — password-gated handler-device delete
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const currentDeviceId = typeof window !== 'undefined' ? localStorage.getItem('admin_device_id') : null;

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [adminRes, handlerRes] = await Promise.all([
        axios.get(`${API}/admin/devices`, { params: { admin_id: user.id } }),
        axios.get(`${API}/admin/handler-devices`, { params: { admin_id: user.id } }).catch(() => ({ data: [] })),
      ]);
      setDevices(Array.isArray(adminRes.data) ? adminRes.data : []);
      setHandlerDevices(Array.isArray(handlerRes.data) ? handlerRes.data : []);
    } catch (e) {
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const toggleTrusted = async (dev) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/devices/${dev.id}`,
        { trusted: !dev.trusted },
        { params: { admin_id: user.id } }
      );
      setDevices((prev) => prev.map((d) => (d.id === dev.id ? { ...d, ...data } : d)));
      toast.success(data.trusted ? 'Device marked as trusted' : 'Trust revoked');
    } catch {
      toast.error('Failed to update device');
    }
  };

  const saveRename = async (dev) => {
    if (!editName.trim()) {
      setEditingId(null);
      return;
    }
    const isHandlerDev = tab === 'handlers';
    const endpoint = isHandlerDev
      ? `${API}/admin/handler-devices/${dev.id}`
      : `${API}/admin/devices/${dev.id}`;
    try {
      const { data } = await axios.patch(endpoint, { device_name: editName.trim() }, { params: { admin_id: user.id } });
      if (isHandlerDev) {
        setHandlerDevices((prev) => prev.map((d) => (d.id === dev.id ? { ...d, device_name: editName.trim() } : d)));
      } else {
        setDevices((prev) => prev.map((d) => (d.id === dev.id ? { ...d, ...data } : d)));
      }
      setEditingId(null);
      toast.success('Renamed');
    } catch {
      toast.error('Failed to rename');
    }
  };

  const removeDevice = async (dev) => {
    const isHandlerDev = tab === 'handlers';
    if (isHandlerDev) {
      // Point 44 — handler device/session records only delete after the
      // admin re-enters their own password.
      setDeletePasswordDev(dev);
      return;
    }
    if (!window.confirm(`Remove "${dev.device_name || 'this device'}" ? You'll need to verify with Google again from this device.`)) return;
    try {
      await axios.delete(`${API}/admin/devices/${dev.id}`, { params: { admin_id: user.id } });
      setDevices((prev) => prev.filter((d) => d.id !== dev.id));
      toast.success('Device removed');
    } catch {
      toast.error('Failed to remove');
    }
  };

  const confirmHandlerDeviceDelete = async () => {
    if (!deletePassword) { toast.error('Password required'); return; }
    setDeleteBusy(true);
    try {
      await axios.post(`${API}/admin/handler-devices/${deletePasswordDev.id}/delete-with-password`, {
        admin_id: user.id, password: deletePassword,
      });
      setHandlerDevices((prev) => prev.filter((d) => d.id !== deletePasswordDev.id));
      toast.success('Device removed');
      setDeletePasswordDev(null);
      setDeletePassword('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove');
    } finally {
      setDeleteBusy(false);
    }
  };

  const openSessions = (dev) => setSessionModal(dev);

  const kickDevice = async (dev) => {
    const reason = window.prompt(
      `Force logout every session on "${dev.device_name || 'this device'}"?\n\nOptional reason (audit log):`,
      ''
    );
    if (reason === null) return;  // user cancelled
    try {
      const { data } = await axios.post(`${API}/admin/devices/${dev.id}/kick`, {
        admin_id: user.id,
        reason: reason || 'Kicked by admin',
      });
      toast.success(`👢 Kicked ${data.kicked_sessions} session(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to kick device');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-registered-devices">
      <AccessCodePanel />
    <div className="bg-white rounded-xl shadow p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Devices size={22} weight="fill" className="text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Registered Devices</h3>
        </div>
        <button
          onClick={load}
          className="text-xs text-purple-600 hover:text-purple-800 font-medium"
          data-testid="devices-refresh-btn"
        >
          Refresh
        </button>
      </div>

      <p className="text-xs text-gray-500 mb-3">
        Trusted devices skip Google verification on future logins. Only mark devices you personally use as trusted.
      </p>

      {/* Admin | Handlers tab switcher */}
      <div className="flex gap-1 mb-3 bg-gray-100 rounded-lg p-1" data-testid="devices-tabs">
        {[
          { key: 'admin', label: `Admin (${devices.length})` },
          { key: 'handlers', label: `Handlers (${handlerDevices.length})` },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              tab === t.key ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-600 hover:bg-white/60'
            }`}
            data-testid={`devices-tab-${t.key}`}
          >{t.label}</button>
        ))}
      </div>

      {(() => {
        const list = tab === 'handlers' ? handlerDevices : devices;
        const isHandler = tab === 'handlers';
        if (loading) return <div className="text-center py-6 text-sm text-gray-500">Loading devices...</div>;
        if (list.length === 0) return <div className="text-center py-8 text-sm text-gray-500">No {isHandler ? 'handler' : 'registered'} devices yet.</div>;
        return (
        <div className="space-y-2">
          {list.map((dev) => {
            const Icon = iconFor(dev.device_name || dev.user_agent);
            const isCurrent = !isHandler && dev.device_id === currentDeviceId;
            const isEditing = editingId === dev.id;
            const totalMinsToday = isHandler ? Math.round((dev.total_seconds_today || 0) / 60) : null;
            return (
              <div
                key={dev.id}
                className={`border rounded-lg p-3 ${
                  !isHandler && dev.trusted ? 'border-green-400 bg-green-50/40' : 'border-gray-200'
                }`}
                data-testid={`device-row-${dev.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Icon size={22} weight="fill" className="text-purple-700" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {isEditing ? (
                        <input
                          autoFocus
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveRename(dev);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          className="border border-gray-300 rounded px-2 py-0.5 text-sm w-40"
                          maxLength={80}
                          data-testid={`device-rename-input-${dev.id}`}
                        />
                      ) : (
                        <span className="font-medium text-gray-900 text-sm truncate">
                          {dev.device_name || (isHandler ? `${dev.handler_username || 'Handler'} · Unnamed` : 'Unnamed device')}
                        </span>
                      )}
                      {isCurrent && (
                        <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded uppercase tracking-wide">
                          This device
                        </span>
                      )}
                      {isHandler && dev.handler_username && (
                        <span className="text-[10px] font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded uppercase tracking-wide">
                          {dev.handler_username}
                        </span>
                      )}
                      {!isHandler && dev.trusted && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-green-100 text-green-700 px-1.5 py-0.5 rounded uppercase tracking-wide">
                          <ShieldCheck size={10} weight="fill" /> Trusted
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-500 truncate mt-0.5">
                      {dev.ip_address || 'IP unknown'} · last seen {formatTime(dev.last_seen)}
                    </div>
                    {isHandler && (
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        {dev.sessions_today || 0} session(s) today · {totalMinsToday} min · {dev.login_count || 0} total logins
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-1 flex-shrink-0">
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => saveRename(dev)}
                          className="p-1.5 text-green-700 hover:bg-green-50 rounded"
                          title="Save"
                          data-testid={`device-rename-save-${dev.id}`}
                        >
                          <Check size={16} weight="bold" />
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                          title="Cancel"
                        >
                          <X size={16} weight="bold" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => openSessions(dev)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="View sessions"
                          data-testid={`device-sessions-btn-${dev.id}`}
                        >
                          <Devices size={16} />
                        </button>
                        <button
                          onClick={() => kickDevice(dev)}
                          className="p-1.5 text-orange-600 hover:bg-orange-50 rounded"
                          title="Kick — force-logout every session"
                          data-testid={`device-kick-btn-${dev.id}`}
                        >
                          <ShieldSlash size={16} weight="bold" />
                        </button>
                        <button
                          onClick={() => { setEditingId(dev.id); setEditName(dev.device_name || ''); }}
                          className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded"
                          title="Rename"
                          data-testid={`device-rename-btn-${dev.id}`}
                        >
                          <PencilSimple size={16} />
                        </button>
                        <button
                          onClick={() => removeDevice(dev)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Remove device"
                          data-testid={`device-remove-btn-${dev.id}`}
                        >
                          <Trash size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* BIG obvious trust toggle button — only for admin's own devices */}
                {!isHandler && !isEditing && (
                  <button
                    onClick={() => toggleTrusted(dev)}
                    className={`mt-3 w-full py-2 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                      dev.trusted
                        ? 'bg-orange-50 text-orange-700 border border-orange-300 hover:bg-orange-100'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                    data-testid={`device-trust-toggle-${dev.id}`}
                  >
                    {dev.trusted ? (
                      <>
                        <ShieldSlash size={16} weight="fill" />
                        Revoke Trust (require Google next time)
                      </>
                    ) : (
                      <>
                        <ShieldCheck size={16} weight="fill" />
                        Mark as Trusted (skip Google next time)
                      </>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        );
      })()}

      {sessionModal && (
        <DeviceSessionsModal device={sessionModal} adminId={user?.id} onClose={() => setSessionModal(null)} />
      )}

      {deletePasswordDev && (
        <div className="fixed inset-0 bg-black/60 z-[130] flex items-center justify-center p-3" onClick={() => !deleteBusy && setDeletePasswordDev(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-sm p-5 shadow-2xl" data-testid="handler-device-delete-modal">
            <h3 className="font-bold text-lg text-slate-800 mb-1">Remove "{deletePasswordDev.device_name || 'this device'}"</h3>
            <p className="text-[12px] text-slate-600 mb-3">Enter your admin password to confirm.</p>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Admin password"
              autoFocus
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-3"
              data-testid="handler-device-delete-password-input"
            />
            <div className="flex gap-2">
              <button onClick={() => { setDeletePasswordDev(null); setDeletePassword(''); }} disabled={deleteBusy} className="flex-1 py-2 border border-slate-300 rounded-lg text-sm">Cancel</button>
              <button
                onClick={confirmHandlerDeviceDelete}
                disabled={deleteBusy || !deletePassword}
                className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50"
                data-testid="handler-device-delete-confirm"
              >{deleteBusy ? 'Removing…' : 'Remove'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

// Session-40: Access Code panel — generates 5-digit OTP with 5-min TTL,
// shows countdown + history. Rendered above the device grid.
const AccessCodePanel = () => {
  const { user } = useAuth();
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  // Session-45: collapse history to 3 rows by default + password-gated bulk delete
  const [expanded, setExpanded] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [delPassword, setDelPassword] = useState('');
  const [delBusy, setDelBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [cur, hist] = await Promise.all([
        axios.get(`${API}/admin/2fa/access-code/current`, { params: { admin_id: user.id } }),
        axios.get(`${API}/admin/2fa/access-code/history`, { params: { admin_id: user.id, limit: 100 } }),
      ]);
      setActive(cur.data?.active || null);
      setHistory(hist.data?.items || []);
    } catch (_) { /* ignore */ }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  // Auto-refresh when a code expires so history moves it out of "active"
  useEffect(() => {
    if (!active?.expires_at) return;
    const exp = new Date(active.expires_at).getTime();
    if (now > exp) load();
  }, [now, active, load]);

  const generate = async () => {
    if (!user?.id) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/2fa/access-code/request`, { admin_id: user.id });
      await load();
      toast.success('🔑 New access code generated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate');
    } finally { setBusy(false); }
  };

  const remaining = active?.expires_at
    ? Math.max(0, Math.floor((new Date(active.expires_at).getTime() - now) / 1000))
    : 0;
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;

  return (
    <div className="bg-white rounded-xl shadow p-5" data-testid="admin-access-code-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">🔑 Access Code</h3>
          <p className="text-[11px] text-gray-500">5-digit OTP · Valid for 5 minutes · Share with another admin session to sign in.</p>
        </div>
        <button onClick={generate} disabled={busy}
                className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-3 py-1.5 rounded disabled:opacity-50"
                data-testid="admin-access-code-generate">
          {busy ? 'Generating…' : 'Generate New'}
        </button>
      </div>

      {active && remaining > 0 ? (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase font-bold text-indigo-500 mb-1">Active Code</div>
            <div className="text-3xl font-mono font-bold tracking-[0.25em] text-indigo-900" data-testid="admin-access-code-value">
              {active.code}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase font-bold text-indigo-500 mb-1">Expires in</div>
            <div className="text-xl font-bold text-indigo-900">{String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}</div>
          </div>
        </div>
      ) : (
        <div className="text-xs text-gray-400 italic mb-3">No active code. Click "Generate New" to create one.</div>
      )}

      <div>
        {/* Session-45: default to LAST 3, expand with "View more", plus a
            password-gated "Delete history" that wipes expired codes. */}
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase font-bold text-gray-500">
            History{expanded ? ` (last ${history.length})` : ' (last 3)'}
          </div>
          <button
            onClick={() => setShowDelete(true)}
            disabled={history.filter(h => h.status !== 'active').length === 0}
            className="text-[10px] font-semibold text-red-600 hover:text-red-800 disabled:text-gray-300 flex items-center gap-1"
            data-testid="access-code-delete-history-btn"
          >
            🗑️ Delete History
          </button>
        </div>
        <div className="max-h-60 overflow-y-auto space-y-1">
          {history.length === 0 ? (
            <div className="text-xs text-gray-400 py-2">No history yet.</div>
          ) : (expanded ? history : history.slice(0, 3)).map(h => (
            <div key={h.id} className="flex items-center justify-between text-[11px] px-2 py-1.5 rounded border border-gray-100 bg-gray-50">
              <span className="font-mono">{h.code}</span>
              <span className={`px-1.5 py-0.5 rounded font-semibold text-[10px] ${
                h.status === 'used' ? 'bg-emerald-100 text-emerald-700'
                : h.status === 'active' ? 'bg-indigo-100 text-indigo-700'
                : 'bg-gray-200 text-gray-600'
              }`}>{h.status}</span>
              <span className="text-gray-500">
                {new Date(h.requested_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          ))}
        </div>
        {history.length > 3 && (
          <button
            onClick={() => setExpanded(v => !v)}
            className="mt-2 w-full text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 py-1 border-t border-gray-100 pt-2"
            data-testid="access-code-view-more-btn"
          >
            {expanded ? '↑ Show less' : `↓ View more (${history.length - 3} more)`}
          </button>
        )}
      </div>

      {/* Session-45: password-gated Delete History modal */}
      {showDelete && (
        <div className="fixed inset-0 bg-black/60 z-[130] flex items-center justify-center p-3"
             onClick={() => !delBusy && setShowDelete(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white rounded-xl w-full max-w-sm p-5 shadow-2xl"
               data-testid="access-code-delete-modal">
            <h3 className="font-bold text-lg text-slate-800 mb-1">🗑️ Delete access-code history</h3>
            <p className="text-[12px] text-slate-600 mb-3">
              Yeh saare <b>expired / used</b> codes permanently delete kar dega.
              Active code safe rahega. Confirm karne ke liye apna admin password
              enter karo.
            </p>
            <input
              type="password"
              value={delPassword}
              onChange={(e) => setDelPassword(e.target.value)}
              placeholder="Admin password"
              autoFocus
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-3"
              data-testid="access-code-delete-password-input"
            />
            <div className="flex gap-2">
              <button
                onClick={() => { setShowDelete(false); setDelPassword(''); }}
                disabled={delBusy}
                className="flex-1 py-2 border border-slate-300 rounded-lg text-sm"
              >Cancel</button>
              <button
                onClick={async () => {
                  if (!delPassword) { toast.error('Password required'); return; }
                  setDelBusy(true);
                  try {
                    const res = await axios.post(`${API}/admin/2fa/access-code/delete-history`, {
                      admin_id: user.id, password: delPassword,
                    });
                    toast.success(`✅ Deleted ${res.data?.deleted || 0} expired codes`);
                    setShowDelete(false);
                    setDelPassword('');
                    setExpanded(false);
                    await load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Delete failed');
                  } finally { setDelBusy(false); }
                }}
                disabled={delBusy || !delPassword}
                className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50"
                data-testid="access-code-delete-confirm"
              >{delBusy ? 'Deleting…' : 'Delete Expired'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Modal that pulls /admin/devices/{id}/sessions and shows each login row for
 * the device's owner, with an optional date filter. Total time is aggregated
 * at the top so admins can see at a glance how long a handler was active.
 */
const DeviceSessionsModal = ({ device, adminId, onClose }) => {
  const [dateFilter, setDateFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let stop = false;
    setLoading(true);
    axios.get(`${API}/admin/devices/${device.id}/sessions`, {
      params: { admin_id: adminId, ...(dateFilter ? { date: dateFilter } : {}) },
    })
      .then((res) => { if (!stop) setData(res.data); })
      .catch(() => { if (!stop) setData({ sessions: [] }); })
      .finally(() => { if (!stop) setLoading(false); });
    return () => { stop = true; };
  }, [device.id, adminId, dateFilter]);

  const fmtSecs = (s) => {
    const h = Math.floor((s || 0) / 3600);
    const m = Math.floor(((s || 0) % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="device-sessions-modal">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div>
            <h4 className="text-base font-semibold text-gray-800">{device.device_name || 'Device'}</h4>
            <p className="text-xs text-gray-500">Login history</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full"><X size={18} /></button>
        </div>
        <div className="px-4 py-2 border-b flex items-center gap-2 bg-gray-50">
          <label className="text-xs text-gray-600">Filter by date:</label>
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-xs"
            data-testid="device-sessions-date-filter"
          />
          {dateFilter && (
            <button onClick={() => setDateFilter('')} className="text-[11px] text-purple-600 hover:text-purple-800 font-medium">Clear</button>
          )}
          <div className="ml-auto text-xs text-gray-700 font-semibold">
            {data ? `${data.session_count} session(s) · ${fmtSecs(data.total_seconds)}` : '—'}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {loading ? (
            <div className="text-center py-6 text-sm text-gray-500">Loading…</div>
          ) : (data?.sessions || []).length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">No sessions {dateFilter ? `on ${dateFilter}` : 'yet'}</div>
          ) : (
            (data.sessions || []).map((s, i) => (
              <div key={i} className="border rounded-lg px-3 py-2 flex items-center justify-between" data-testid={`device-session-${i}`}>
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-gray-800 truncate">
                    {new Date(s.login_time).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div className="text-[10px] text-gray-500">{s.date}</div>
                </div>
                <div className="text-right ml-2">
                  <div className="text-xs font-bold text-purple-700">{fmtSecs(s.duration_seconds)}</div>
                  {s.is_active && <div className="text-[10px] text-green-600 font-semibold">Active</div>}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisteredDevicesSection;
